import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGoUp, useWorkspaceNavigate, useWorkspacePath } from '../../workspace-path';
import {
  fileAttachmentSchema,
  taskCommentSchema,
  taskDependencySchema,
  lineItemSchema,
  metafieldSchema,
  overtimeTypeSchema,
  taskIssueLinkSchema,
  taskLinkSchema,
  taskPrioritySchema,
  taskSchema,
  taskStatusSchema,
  workspaceMemberSchema,
  worklogSchema,
  type FileAttachment,
  type OvertimeType,
  type Task,
  type Worklog,
} from '@forecastify/shared';
import { useDuration } from '../../duration';
import { useT } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { TASK_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { escapeHtml } from '../escape-html';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { parseList, parseOne, usePermissionSet } from '../permissions-hook';
import { readAll } from '../read-all';
import { TaskSurface, type TaskMode } from './task-surface';
import type { TaskDetailData } from './task-detail';
import { worklogBody, worklogHours, type WorklogDraft } from '../time/worklog-form';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Loading a task and driving its three surfaces.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The full-page mode is a ROUTE; the other two are overlays over whatever you were doing.**
 * That is what makes `/tasks/HSO-131` a link somebody can send, and it is why `mode` starts at
 * `full` here — this component *is* the address. `useTaskOverlay` below is the other half: the
 * drawer and modal a list opens without navigating.
 *
 * ⚠️ **The address carries the KEY, not the uuid.** `HSO-131` is what people paste out of a commit
 * message; a uuid in a URL is a link nobody can read or type. The API has a first-class route for
 * it (`GET /tasks/key/:key`) so this is a lookup rather than a guess.
 */

/** Everything the detail needs, loaded together. */
export function useTaskData(taskId: string | null): {
  data: TaskDetailData | null;
  failure: ScreenFailure | null;
  reload: () => Promise<void>;
} {
  const session = useSession();
  const [data, setData] = useState<TaskDetailData | null>(null);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (taskId === null) {
      setData(null);
      setFailure(null);
      return;
    }
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    const response = await session.request(`/tasks/${taskId}`).catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }

    const task = taskSchema.safeParse(await response.json());
    if (!task.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }

    /**
     * ⚠️ **The requests, issued CONCURRENTLY rather than in sequence.** They do not depend on
     * each other, and awaiting them one at a time makes opening a task nine round trips deep — on
     * a drawer that is supposed to feel instant.
     *
     * ⚠️ **A failed one yields an EMPTY LIST rather than failing the screen** (see `fetchList`):
     * a caller without `people.view` gets a 404 from `/members` by rule 5, and should still see
     * the task, just without a picker. Only the task read itself decides the screen.
     */
    const [
      statuses,
      priorities,
      members,
      projects,
      links,
      dependencies,
      comments,
      subtasks,
      metafields,
      lineItems,
      issueLink,
    ] = await Promise.all([
      fetchList(session, '/task-statuses', taskStatusSchema),
      fetchList(session, '/task-priorities', taskPrioritySchema),
      fetchList(session, '/members', workspaceMemberSchema),
      fetchList(session, '/projects', projectRowSchema),
      fetchList(session, `/tasks/${taskId}/links`, taskLinkSchema),
      fetchList(session, `/tasks/${taskId}/dependencies`, taskDependencySchema),
      fetchList(session, `/tasks/${taskId}/comments`, taskCommentSchema),
      fetchList(session, `/tasks?parentTaskId=${taskId}`, taskSchema),
      fetchList(session, '/metafields?entity=task', metafieldSchema),
      /* ⚠️ C25 · **non-fatal like every other list here** — `fetchList` answers `[]` on a refusal,
       * which is exactly right for a tenant without contracts: the picker is not drawn at all. */
      fetchList(session, '/line-items', lineItemSchema),
      /**
       * C36 · where this task came from.
       *
       * ⚠️ **`null` on a refusal or a miss, like every other side-load here.** Most tasks have no
       * remote issue and the row is simply absent; a caller without `task.view` never reaches this
       * line anyway, because the task read above already failed the screen.
       */
      fetchOne(session, `/integrations/tasks/${taskId}/link`, taskIssueLinkSchema),
    ]);

    /**
     * C11's hours, C12's envelope.
     *
     * ⚠️ **`limit=50` is the default and it is left at the default deliberately.** A task with more
     * than fifty entries exists, and the footer says when the list is one page of more — while the
     * *total* comes from `task.trackedHours`, which is a SQL sum and therefore right regardless.
     * Asking for 200 here would only move the cliff, and hide it further away.
     */
    const worklogPage = await fetchPage(session, `/worklogs?taskId=${taskId}`, worklogSchema);

    /**
     * ⚠️⚠️ **The assignee list is the PROJECT'S members, not the workspace's — P-1.** Offering a
     * name the server will refuse is worse than not offering it, and the mockup's `assigneeItems`
     * lists everybody. A task with **no project** has no membership constraint, so it gets the
     * workspace list — which is P-1's own conditional rather than a shortcut.
     */
    const assignees =
      task.data.projectId === null
        ? members.map((member) => ({ id: member.id, name: member.displayName }))
        : (
            await fetchList(
              session,
              `/projects/${task.data.projectId}/members`,
              projectMemberRowSchema,
            )
          )
            /* R2 · 3 Nov 2026 · nor a deactivated person — except the one the task already has,
               so the picker does not clear them. */
            .filter(
              (member) =>
                (member.active && member.memberStatus === 'active') ||
                member.memberId === task.data.assigneeMemberId,
            )
            .map((member) => ({ id: member.memberId, name: member.displayName }));

    setFailure(null);
    setData({
      task: task.data,
      statuses,
      lineItems,
      priorities,
      assignees,
      /* ⚠️ **The WORKSPACE's members, not the project's, and the difference is P-1's.** The
       * assignee picker above is restricted to the project's people because the server refuses
       * anything else; a mention is not an assignment and carries no membership rule, so mentioning
       * a colleague who is not on the project is legitimate — asking a specialist to look at
       * something is most of what a mention is for. */
      people: members,
      projects: projects.map((project) => ({ id: project.id, name: project.name })),
      subtasks,
      links,
      dependencies,
      comments,
      /* ⚠️ Item 73 · who is looking, so a comment's edit and delete appear only on their OWN.
         The member id is per-WORKSPACE — `session.workspaces` carries one each. */
      currentMemberId:
        session.workspaces.find((entry) => entry.id === session.workspaceId)?.memberId ?? null,
      metafields,
      issueLink,
      worklogs: worklogPage.rows,
      worklogsPartial: worklogPage.more,
    });
  }, [session, taskId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, failure, reload };
}

/* Only the two fields the picker needs — the full schemas live in their own contracts. */
const projectRowSchema = {
  safeParse: (value: unknown): { success: boolean; data?: { id: string; name: string } } => {
    const row = value as { id?: unknown; name?: unknown };
    if (typeof row.id !== 'string' || typeof row.name !== 'string') return { success: false };
    return { success: true, data: { id: row.id, name: row.name } };
  },
};

const projectMemberRowSchema = {
  safeParse: (
    value: unknown,
  ): {
    success: boolean;
    data?: { memberId: string; displayName: string; active: boolean; memberStatus: string };
  } => {
    const row = value as {
      memberId?: unknown;
      displayName?: unknown;
      active?: unknown;
      memberStatus?: unknown;
    };
    if (
      typeof row.memberId !== 'string' ||
      typeof row.displayName !== 'string' ||
      typeof row.active !== 'boolean'
    ) {
      return { success: false };
    }
    return {
      success: true,
      data: {
        memberId: row.memberId,
        displayName: row.displayName,
        active: row.active,
        /* R4 · the person's own status; absent from an older server reads as active. */
        memberStatus: typeof row.memberStatus === 'string' ? row.memberStatus : 'active',
      },
    };
  },
};

/**
 * ⚠️ **A failed side-load yields an EMPTY LIST, not a screen failure.** These are the pickers and
 * the activity pane; a caller who cannot read `/members` should still see the task. The task read
 * itself is the one that fails the screen, and it is handled above.
 *
 * ⚠️ **Every page (30 Oct 2026).** One page was fifty members and fifty projects — the assignee
 * picker of an 87-person workspace offered fifty-odd of them (`read-all.ts`).
 */
async function fetchList<T>(
  session: ReturnType<typeof useSession>,
  path: string,
  schema: { safeParse: (value: unknown) => { success: boolean; data?: T } },
): Promise<T[]> {
  return (await readAll(session, path, schema).catch(() => null)) ?? [];
}

/** The same, for an endpoint whose `data` is one object or `null`. */
async function fetchOne<T>(
  session: ReturnType<typeof useSession>,
  path: string,
  schema: { safeParse: (value: unknown) => { success: boolean; data?: T } },
): Promise<T | null> {
  const response = await session.request(path).catch(() => null);
  if (response?.ok !== true) return null;
  return parseOne(schema, await response.json());
}

/**
 * The same, for an endpoint that returns `{ data, meta: { next_cursor } }`.
 *
 * ⚠️ **It reports whether there is more, and the caller has to do something with that.** Discarding
 * `meta` is how a paged list becomes a screen that quietly shows the first fifty of anything —
 * `state-rules.md` §6 wants it said in the footer, so the flag has to reach the footer.
 */
async function fetchPage<T>(
  session: ReturnType<typeof useSession>,
  path: string,
  schema: { safeParse: (value: unknown) => { success: boolean; data?: T } },
): Promise<{ rows: T[]; more: boolean }> {
  const response = await session.request(path).catch(() => null);
  if (response?.ok !== true) return { rows: [], more: false };

  const body: unknown = await response.json();
  const rows = parseList(schema, body) ?? [];
  const meta = (body as { meta?: { next_cursor?: unknown } }).meta;
  return { rows, more: typeof meta?.next_cursor === 'string' };
}

/**
 * A description with one file's anchor taken out.
 *
 * ⚠️ **Parsed rather than regexed**, and the emptied paragraph goes with the anchor —
 * `task-detail.tsx`'s `splitDescription` argues both at length and this is its inverse.
 *
 * ⚠️ **An emptied description becomes `null`, not `''`.** The column is nullable and the screen's
 * empty state reads `=== null`; an empty string would draw a blank box with no invitation in it.
 */
function withoutFileLink(html: string | null, fileId: string): string | null {
  if (html === null || html === '') return html;

  const parsed = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  for (const anchor of parsed.querySelectorAll(`a[href*="/files/${fileId}"]`)) {
    const paragraph = anchor.closest('p');
    anchor.remove();
    if (paragraph !== null && paragraph.textContent.trim() === '') paragraph.remove();
  }

  const next = parsed.body.innerHTML;
  return next.replace(/<[^>]*>/gu, '').trim() === '' ? null : next;
}

/** Writing a patch, and turning a refusal into a sentence. */
export function useTaskWrites(
  taskId: string | null,
  version: number | undefined,
  reload: () => Promise<void>,
): {
  patch: (patch: Record<string, unknown>) => void;
  comment: (html: string, mentionMemberIds: readonly string[], files?: readonly File[]) => void;
  /** ⚠️ Resolves once the files have landed, so the attachment list can re-read. */
  attachFiles: (files: readonly File[]) => Promise<void>;
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ A FILE DROPPED ON THE DESCRIPTION GOES INTO THE DESCRIPTION.         │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * Asked for on 15 Sep, twice. It attaches to the task like any other drop — the file has to live
   * somewhere, and the ticket's folder is where — and then **appends a link to it** to the
   * description's HTML, so the text says what was dropped into it.
   *
   * ⚠️ **The current HTML is a parameter rather than read here**, because this hook has the task's
   * id and version and not its body. Passing it keeps one source of truth: whatever the screen is
   * showing is what gets appended to.
   */
  attachToDescription: (files: readonly File[], html: string | null) => Promise<void>;
  /**
   * The chip's ×: delete the file and take its mention out of the text, in that order.
   *
   * ⚠️ **One act, because in this model they are one thing.** A file in the description lives there
   * and nowhere else (`0117`), so there is no state in which the file exists and the text does not
   * mention it — the Csatolmányok list will never show it and nothing else can reach it.
   */
  removeDescriptionFile: (fileId: string, html: string | null) => void;
  /**
   * ⚠️ Item 73 · editing and deleting a comment. The server refuses anybody else's
   * (`NotYourCommentError`), which is why the screen offers these only on your own.
   */
  editComment: (
    comment: { id: string; version: number },
    html: string,
    mentionMemberIds: readonly string[],
  ) => void;
  deleteComment: (comment: { id: string }) => void;
  logTime: (draft: WorklogDraft) => void;
  deleteWorklog: (entry: Worklog) => void;
  createSubtask: (title: string) => void;
  detachSubtask: (subtask: Task) => void;
  addLink: (otherTaskId: string) => void;
  removeLink: (linkId: string) => void;
  addDependency: (predecessorId: string, lagDays: number) => void;
  removeDependency: (dependencyId: string) => void;
  error: string | null;
} {
  const session = useSession();
  const t = useT();
  const { unit: durationUnit } = useDuration();
  const [error, setError] = useState<string | null>(null);

  /**
   * One write, its refusal turned into a sentence, and NO reload.
   *
   * ⚠️ **Split out of `send` for C22.** Posting a comment with attachments is three writes that
   * have to happen in order — create, upload each file, *then* reload — because the reload is what
   * mounts the new comment, and each comment's attachment list fetches on mount. Reloading between
   * the create and the uploads mounts the list against a comment that has no files yet, and it has
   * nothing to tell it to look again.
   */
  /* ⚠️ The workspace-scoped href for a file link — `files/file-route.tsx` says why the link points
     at the app rather than at the API. */
  const at = useWorkspacePath();

  const write = async (path: string, init: RequestInit): Promise<unknown> => {
    setError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setError(t('error.unavailable'));
      return null;
    }
    if (!response.ok) {
      const envelope = (await response.json().catch(() => null)) as {
        error?: { code?: string; details?: { message?: string }[] };
      } | null;
      const code = envelope?.error?.code ?? 'unavailable';
      const detail = envelope?.error?.details?.[0]?.message ?? '';

      /* ⚠️ Branching on `code`, never on `message` — rule 5. P-1's refusal carries the name. */
      if (code === 'assignee_not_a_project_member') {
        setError(t('error.assignee_not_a_project_member', { name: detail }));
      } else if (code === 'not_a_project_member') setError(t('time.error.notAMember'));
      /* 26 Oct 2026 · the person has *Időt rögzít* off — the server refuses their hours. */
      else if (code === 'member_not_tracking')
        setError(t('time.error.notLogging', { name: detail }));
      else if (code === 'outside_membership_window') setError(t('time.error.outsideWindow'));
      else if (code === 'permission_denied') setError(t('time.error.notPermitted'));
      /* C13's three, which the log-time widget can provoke as readily as the Time screens can. */
      else if (code === 'day_over_maximum') setError(t('time.error.dayFullShort'));
      else if (code === 'outside_backdate_window') setError(t('time.error.tooFarBackShort'));
      else if (code === 'month_closed') setError(t('time.error.monthClosedShort'));
      /* ⚠️ A settled overtime claim freezes its hour — the same class of no. */
      else if (code === 'overtime_approved') setError(t('time.error.overtimeApproved'));
      else if (code === 'would_cycle') setError(t('error.would_cycle'));
      /* U-7 · a licence line item takes no task and no hour — a move onto one, or a log. */
      else if (code === 'line_item_takes_no_work') setError(t('error.line_item_takes_no_work'));
      /* IP-3 · a move onto a closed line item. */
      else if (code === 'line_item_closed') setError(t('error.line_item_closed', { name: detail }));
      else if (code === 'contract_closed') setError(t('error.contract_closed', { name: detail }));
      /* R2 · 3 Nov 2026 · a deactivated assignee. */
      else if (code === 'member_not_active') {
        setError(t('error.member_not_active', { name: detail }));
      }
      /* J-2 · the tracker's status has no counterpart here, so the status cannot move. */
      else if (code === 'task_status_unmapped') {
        setError(t('error.task_status_unmapped', { status: detail }));
      }
      /* J-6 · a tag the tracker has no label for, on a synced task. */
      else if (code === 'task_tag_not_in_tracker') {
        setError(t('error.task_tag_not_in_tracker', { tag: detail }));
      } else if (code === 'conflict') setError(t('error.conflict'));
      else if (code === 'validation_failed') setError(t('task.error.estimate'));
      else if (code === 'not_found') setError(t('error.not_found'));
      /* C22's four, which an attachment upload can answer with. */
      else if (code === 'file_too_large') setError(t('task.attachment.tooLarge'));
      else if (code === 'file_infected') setError(t('task.attachment.infected'));
      else if (code === 'scanner_unavailable') setError(t('task.attachment.scannerDown'));
      else if (code === 'unsupported_file_type') setError(t('task.attachment.failed'));
      else setError(t('error.unavailable'));
      return null;
    }
    return (await response.json().catch(() => null)) ?? {};
  };

  /**
   * Put files on the task, one at a time, and answer with what was created.
   *
   * ⚠️ **Sequential, for the reason the comment path records**: each file is scanned, and firing
   * five at one clamd is how a scan times out and refuses a file that was fine.
   *
   * ⚠️ **It returns the rows** — which `attachFiles` ignores and `attachToDescription` needs, since
   * a link has to name the id the server chose.
   */
  const upload = async (
    files: readonly File[],
    target: 'task' | 'task_description',
  ): Promise<readonly FileAttachment[]> => {
    if (taskId === null) return [];
    const added: FileAttachment[] = [];

    for (const file of files) {
      const form = new FormData();
      form.set('file', file);
      const sent = await write(`/files?target=${target}&targetId=${taskId}`, {
        method: 'POST',
        body: form,
      });
      /* ⚠️ A refusal stops the run rather than carrying on: the first failure is usually the
         scanner or the size limit, and the rest would fail the same way and stack five toasts. */
      if (sent === null) break;
      const parsed = fileAttachmentSchema.safeParse(sent);
      if (parsed.success) added.push(parsed.data);
    }

    return added;
  };

  /** The ordinary case: one write, then re-read what it changed. */
  const send = async (path: string, init: RequestInit): Promise<void> => {
    if ((await write(path, init)) === null) return;
    await reload();
  };

  return {
    error,
    patch: (patch) => {
      if (taskId === null || version === undefined) return;
      void send(`/tasks/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify({ ...patch, version }),
      });
    },
    /**
     * ⚠️ **C22 · the comment and its attachments are ONE action to whoever wrote them**, and
     * three writes underneath: the comment first, because a file has to be linked to something that
     * exists, then each file, then a single reload. A failed upload leaves the comment — which is
     * the honest outcome, and the error names which half went wrong.
     */
    comment: (html, mentionMemberIds, files = []) => {
      if (taskId === null) return;
      void (async () => {
        const created = (await write(`/tasks/${taskId}/comments`, {
          method: 'POST',
          body: JSON.stringify({ bodyHtml: html, mentionMemberIds }),
        })) as { id?: string } | null;
        if (created === null) return;

        for (const file of files) {
          if (typeof created.id !== 'string') break;
          const form = new FormData();
          form.set('file', file);
          /* ⚠️ Sequential, not `Promise.all`: each upload is scanned, and firing five 15 MB
           * files at one clamd is how a scan times out and refuses a file that was fine. */
          const sent = await write(`/files?target=task_comment&targetId=${created.id}`, {
            method: 'POST',
            body: form,
          });
          if (sent === null) break;
        }

        await reload();
      })();
    },
    /**
     * ⚠️ **A drop on the task surface — `scoped-drop.tsx`.** The same sequential upload the comment
     * path makes, and for the same reason: each file is scanned, and firing five at one clamd is
     * how a scan times out and refuses a file that was fine.
     *
     * ⚠️ **It resolves rather than returning void**, so the attachment list knows to re-read. It
     * owns its own state and has no other way to hear that something landed in it.
     */
    attachFiles: async (files: readonly File[]) => {
      await upload(files, 'task');
      await reload();
    },

    /**
     * ⚠️⚠️ **THE LINK IS AN APP ADDRESS, NOT AN API ONE** — `files/file-route.tsx` argues it at
     * length. `/{workspace}/files/{id}` is a screen that fetches the bytes with the session that is
     * already there, so nothing about this link is a grant: a reader without permission gets the
     * same refusal the API would give them.
     *
     * ⚠️⚠️ **THE FILENAME IS ESCAPED BEFORE IT BECOMES MARKUP, AND THAT IS NOT OPTIONAL.** It is a
     * string an uploader chose; a file called `<img src=x onerror=...>.pdf` would otherwise be
     * written straight into stored HTML. The server sanitises on the way in as well — this is the
     * belt to that pair of braces, and the cheaper of the two to get right.
     *
     * ⚠️ **Appended, never replacing.** Somebody dropping a file onto a description they have
     * written must not lose the writing.
     */
    attachToDescription: async (files: readonly File[], html: string | null) => {
      /**
       * ⚠️⚠️ **`task_description`, NOT `task` — `0117`.** The first build attached to the ticket and
       * linked to it, which put one file in two places: the text mentioned it and the Csatolmányok
       * list held it. A file in the description is part of what the ticket SAYS rather than a
       * document filed against it, and it has no business in a list of the ticket's documents.
       */
      const added = await upload(files, 'task_description');
      if (added.length === 0) return;

      const links = added
        .map(
          (file) => `<p><a href="${at(`/files/${file.id}`)}">${escapeHtml(file.filename)}</a></p>`,
        )
        .join('');

      await write(`/tasks/${taskId ?? ''}`, {
        method: 'PATCH',
        body: JSON.stringify({ description: `${html ?? ''}${links}`, version }),
      });
      await reload();
    },
    removeDescriptionFile: (fileId, html) => {
      if (taskId === null) return;

      void (async () => {
        /**
         * ⚠️⚠️ **THE TEXT FIRST, THE FILE SECOND, AND THE ORDER MATTERS.** Deleting the blob first
         * and then failing to patch leaves a chip pointing at nothing — the exact failure reported
         * on 15 Sep, arrived at from the other direction. Patching first and then failing to delete
         * leaves an orphaned row that `blob-sweep` collects, which nobody sees.
         */
        const next = withoutFileLink(html, fileId);
        if (next !== html) {
          const patched = await write(`/tasks/${taskId}`, {
            method: 'PATCH',
            body: JSON.stringify({ description: next, version }),
          });
          if (patched === null) return;
        }

        await session.request(`/files/${fileId}`, { method: 'DELETE' }).catch(() => null);
        await reload();
      })();
    },

    editComment: (comment, html, mentionMemberIds) => {
      if (taskId === null) return;
      /* ⚠️ **`PUT` with the VERSION** — a comment is a mutable business row and carries one
         (CLAUDE.md §3), so an edit over somebody's concurrent edit is a 409 rather than a
         silent overwrite. */
      void send(`/tasks/${taskId}/comments/${comment.id}`, {
        method: 'PUT',
        body: JSON.stringify({ bodyHtml: html, mentionMemberIds, version: comment.version }),
      });
    },
    deleteComment: (comment) => {
      if (taskId === null) return;
      void send(`/tasks/${taskId}/comments/${comment.id}`, { method: 'DELETE' });
    },
    /**
     * ⚠️ **`/worklogs`, not `/tasks/:id/worklogs`.** An entry belongs to a task, but it is the
     * worklog resource that owns the rules — P-1, the scope narrowing, C13's window — and a second
     * address for the same write is a second place to forget one of them.
     */
    logTime: (draft) => {
      if (taskId === null) return;
      const hours = worklogHours(draft, durationUnit);
      if (hours === null) return;
      void (async () => {
        const created = (await write('/worklogs', {
          method: 'POST',
          body: JSON.stringify({ taskId, ...worklogBody(draft, hours) }),
        })) as { id?: string } | null;
        if (created === null) return;
        await reload();

        /**
         * ⚠️⚠️ **TWO WRITES, AND THE ORDER IS THE POINT** — `time-route.tsx` argues it at
         * length and this is the same pair. A claim points at a worklog, so the hour has to exist
         * first; there is no endpoint that creates both. The hour is therefore saved even if the
         * claim is refused, which is the right way round: an hour that was worked is a fact, and
         * the claim about it is a request. Feedback round 4 item 20 is what brought the second
         * write here — this form could not mark overtime at all.
         */
        if (!draft.overtime || typeof created.id !== 'string') return;
        await write('/overtime', {
          method: 'POST',
          /* ⚠️ The worklog and nothing else — `0147`. */
          body: JSON.stringify({ worklogId: created.id }),
        });
        await reload();
      })();
    },
    deleteWorklog: (entry) => {
      void send(`/worklogs/${entry.id}`, { method: 'DELETE' });
    },
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ Relations: subtasks, links, dependencies.                             │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **THE WHOLE BACKEND FOR THESE HAS EXISTED SINCE C8 AND NOTHING EVER CALLED IT.** The
     * endpoints, the cycle checks, the audit rows and their tests all landed with the entity; the
     * detail rendered the three lists read-only and offered no way to add or remove one. So the
     * feature was complete on the server, invisible on the screen, and read as unbuilt.
     *
     * ⚠️ **Every one of them re-reads the task afterwards through `send`'s reload.** A link is
     * symmetric and a subtask changes the parent's counts, so the response to the write is not
     * enough to leave the screen correct.
     */
    createSubtask: (title) => {
      if (taskId === null) return;
      /* ⚠️ **No `projectId`, deliberately: the SERVER takes the parent's.** Sending one would be
       * this screen's opinion about a rule the service owns — and sending the wrong one is now a
       * `subtask_project_mismatch` rather than a silently misfiled task. */
      void send('/tasks', {
        method: 'POST',
        body: JSON.stringify({ title, parentTaskId: taskId }),
      });
    },
    /**
     * ⚠️⚠️ **DETACH, NOT DELETE — the mockup's own `taskPatch(s.k, { parent: null })`.** *"Önálló
     * jegy lett"*: the subtask survives as a task of its own. Removing a relationship and
     * destroying work are different actions, and a row menu is not where the second belongs.
     *
     * ⚠️ **The CHILD's version, not the parent's** — this patches the child.
     */
    detachSubtask: (subtask) => {
      void send(`/tasks/${subtask.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ parentTaskId: null, version: subtask.version }),
      });
    },
    addLink: (otherTaskId) => {
      if (taskId === null) return;
      void send(`/tasks/${taskId}/links`, {
        method: 'POST',
        body: JSON.stringify({ taskId: otherTaskId }),
      });
    },
    removeLink: (linkId) => {
      if (taskId === null) return;
      void send(`/tasks/${taskId}/links/${linkId}`, { method: 'DELETE' });
    },
    /**
     * ⚠️ **`predecessorId` is the task that must finish FIRST**, and the one being viewed is always
     * the successor. The endpoint has no way to express the other direction, which is deliberate:
     * *"this waits for that"* said from one side is the whole edge, and offering both would give
     * two ways to write one row.
     */
    addDependency: (predecessorId, lagDays) => {
      if (taskId === null) return;
      void send(`/tasks/${taskId}/dependencies`, {
        method: 'POST',
        body: JSON.stringify({ predecessorId, lagDays }),
      });
    },
    removeDependency: (dependencyId) => {
      if (taskId === null) return;
      void send(`/tasks/${taskId}/dependencies/${dependencyId}`, { method: 'DELETE' });
    },
  };
}

/**
 * Finding another task by key or title.
 *
 * ⚠️ **`/tasks?search=` already exists and is applied in SQL** — the same clause the task list's
 * own search box uses, over key and title. Nothing new on the server for this.
 *
 * ⚠️ **A failed search yields an EMPTY LIST rather than throwing.** The picker is a dialog over a
 * task somebody is reading; a network blip should leave them with "nothing matched" and a working
 * screen behind it, not an error boundary over their work.
 */
/**
 * The picker's search.
 *
 * ⚠⚠️ **`relatableTo` IS THE ANCHOR TASK, AND WITHOUT IT THE PICKER OFFERS TASKS THE SERVER
 * WILL REFUSE.** A task inside a project relates only to that project's tasks, and a task with no
 * project only to other tasks with no project — `requireRelatable` enforces it on the write. The
 * rule is expressed in SQL once, on the server, and passed here as an id rather than reconstructed
 * on the client: a second copy would drift the first time the rule changed, and the symptom would
 * be a picker that lists a task and a `conflict` toast the moment you choose it.
 */
export function useTaskSearch(relatableTo?: string): (query: string) => Promise<readonly Task[]> {
  const session = useSession();
  return useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      const params = new URLSearchParams();
      if (trimmed !== '') params.set('search', trimmed);
      if (relatableTo !== undefined) params.set('relatableTo', relatableTo);
      const suffix = params.size === 0 ? '' : `?${params.toString()}`;
      return fetchList(session, `/tasks${suffix}`, taskSchema);
    },
    [session, relatableTo],
  );
}

/* ── the route ─────────────────────────────────────────────────────────────────────────────── */

export function TaskRoute() {
  const params = useParams();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  /**
   * ⚠️ **`useGoUp`, not a bare `navigate(-1)`** — which did nothing on a task opened from a
   * pasted link or a new tab, the first entry in its history. The arrow goes up to the task list
   * (R-61, 3 Nov 2026).
   */
  const goUp = useGoUp();
  const permissions = usePermissionSet();
  const key = params.key ?? '';

  const [taskId, setTaskId] = useState<string | null>(null);
  const [mode, setMode] = useState<TaskMode>('full');
  const [lookupFailure, setLookupFailure] = useState<ScreenFailure | null>(null);
  /**
   * ⚠️ **The overtime types, for the log widget's picker** — feedback round 4 item 20. `listTypes`
   * is gated on `worklog.read` rather than `settings.view` precisely so a logging control can offer
   * them; `time-route.tsx` reads them the same way. Not fatal: without them the picker is absent
   * and an hour is still an hour.
   */
  const [overtimeTypes, setOvertimeTypes] = useState<readonly OvertimeType[]>([]);
  const session = useSession();

  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const response = await session.request('/overtime/types').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(overtimeTypeSchema, await response.json());
      if (parsed !== null) setOvertimeTypes(parsed.filter((type) => type.active));
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
  }, [session.workspaceId]);

  /* The address carries the key; everything below wants the id. One lookup, at the boundary. */
  useEffect(() => {
    /* ⚠️ A mutable object rather than a `let` flag — see `permissions-hook.ts` for the full note:
     * TypeScript narrows a `let` to `true` inside the closure, so the guard reads as dead. */
    const alive = { current: true };

    void (async () => {
      if (session.workspaceId === null || key === '') return;
      const response = await session.request(`/tasks/key/${key}`).catch(() => null);
      if (response === null) {
        if (alive.current) setLookupFailure({ kind: 'error', code: 'unavailable' });
        return;
      }
      if (!response.ok) {
        const failure = await failureOf(response);
        if (alive.current) setLookupFailure(failure);
        return;
      }
      const parsed = taskSchema.safeParse(await response.json());
      if (parsed.success && alive.current) {
        setLookupFailure(null);
        setTaskId(parsed.data.id);
      }
    })();

    return () => {
      alive.current = false;
    };
  }, [session, key]);

  const { data, failure, reload } = useTaskData(taskId);

  /* ⚠️ C21 · the detail is the screen two people are most likely to hold open at once — a
   * comment, a status move, a worklog against it. R-19. */
  useLiveInvalidation(TASK_TOPICS, reload);
  const writes = useTaskWrites(taskId, data?.task.version, reload);
  const searchTasks = useTaskSearch(taskId ?? undefined);

  return (
    <ScreenState failure={lookupFailure ?? failure} onRetry={() => void reload()}>
      {data === null ? null : (
        <TaskSurface
          mode={mode}
          open
          data={data}
          /* ⚠️ R-38 · workspace-relative, so `go` supplies the first segment. The breadcrumb hands
             back a path rather than navigating itself — see `TaskSurfaceProps.onNavigate`. */
          onNavigate={(path) => {
            go(path);
          }}
          onModeChange={(next) => {
            /**
             * ⚠⚠️ **SHRINKING LEAVES THE ADDRESS AND RE-OPENS THE TASK AS AN OVERLAY ON THE
             * LIST.** The overlay cannot render here: it would sit on top of the same task already
             * rendered underneath it, which is why this used to navigate away instead — to the
             * PROJECT, dropping the task entirely. So the buttons appeared to work and quietly lost
             * what you were looking at.
             *
             * `/tasks?open=<key>&mode=<frame>` carries the intent across the navigation, which also
             * makes "this task, in a drawer, over the list" a linkable state.
             */
            if (next === 'full') {
              setMode('full');
              return;
            }
            go(`/tasks?open=${encodeURIComponent(data.task.key)}&mode=${next}`);
          }}
          onClose={() => {
            goUp('/tasks');
          }}
          handlers={{
            canEdit: permissions.has('task.update'),
            /* ⚠️ The COUNT, not the list — `0147`. */
            overtimeOffered: overtimeTypes.length > 0,
            error: writes.error,
            canLogTime: permissions.has('worklog.create'),
            canEditWorklog: permissions.has('worklog.update'),
            onPatch: writes.patch,
            onAddComment: writes.comment,
            /* ⚠️ Item 73 · only offered on your own comment — `task-detail.tsx` says why. */
            onEditComment: (comment, html, mentions) => {
              writes.editComment(comment, html, mentions);
            },
            onDeleteComment: (comment) => {
              writes.deleteComment(comment);
            },
            onLogTime: writes.logTime,
            onDeleteWorklog: writes.deleteWorklog,
            canSchedule: permissions.has('task.schedule'),
            onCreateSubtask: writes.createSubtask,
            onDetachSubtask: writes.detachSubtask,
            onAddLink: writes.addLink,
            onRemoveLink: writes.removeLink,
            onAddDependency: writes.addDependency,
            onRemoveDependency: writes.removeDependency,
            onSearchTasks: searchTasks,
            onAttachFiles: writes.attachFiles,
            onAttachToDescription: writes.attachToDescription,
            onRemoveDescriptionFile: writes.removeDescriptionFile,
            onOpenTask: (task: Task) => {
              go(`/tasks/${task.key}`);
            },
          }}
        />
      )}
    </ScreenState>
  );
}
